<template>
  <button :disabled="!latestReview?.isLocked" @click="createReviewUpdate">
    <div
      v-tooltip.left="
        latestReview?.isLocked ? '' : 'Please approve review to enable updating'
      "
      class="px-4 py-3 flex items-center gap-2 hover:bg-light-neutral-500 w-full"
      :class="{
        'text-dark-neutral-200 cursor-not-allowed': !latestReview?.isLocked,
      }"
    >
      <ArrowPathIcon class="size-5" />
      <a class="font-medium relative text-sm">
        Smart update
        <span class="absolute inset-0" />
      </a>
    </div>
  </button>
</template>

<script lang="ts" setup>
import useBuiltInImportSources from '@app/composables/use-built-in-import-sources'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { ReviewsServiceKey } from '@app/injectionKeys'
import { injectStrict } from '@app/utils/injectStrict'
import useProjects from '@app/views/Projects/use-projects'
import { Review } from '@core/domain/models/review.model'
import { BuiltInImportSourceId } from '@core/domain/types/builtInImportSourceId'
import { ReviewItemType } from '@core/domain/types/reviewItemType.type'
import { ArrowPathIcon } from '@heroicons/vue/24/solid'
import { PubmedApiClient } from '@infrastructure/pubmedApi/pubmedApi.client'
import { computed } from 'vue'

const reviewsService = injectStrict(ReviewsServiceKey)
const { refresh } = useProjects()
const { reviewSeries } = defineProps<{
  reviewSeries: {
    id: string
    name: string
    order: string | null
    reviews: {
      id: number
      createdAt: Date
      isLocked: boolean
    }[]
  }
}>()
const loading = useLoading()
const snackbar = useSnackbar()
const pubmedApiClient = new PubmedApiClient()
const builtInImportSources = useBuiltInImportSources()
const latestReview = computed(() => {
  if (reviewSeries.reviews.length === 0) {
    return null
  }
  return reviewSeries.reviews.reduce((latest, review) =>
    review.createdAt > latest.createdAt ? review : latest,
  )
})
async function createReviewUpdate() {
  if (!latestReview.value) return

  loading.start()
  try {
    const reviewId = await reviewsService.createReviewUpdate(reviewSeries.id)
    const sourceReviewWithSearches = await reviewsService.findById(
      latestReview.value.id,
    )
    const review = await reviewsService.findById(reviewId)
    const sourceReviewPubmedSearches =
      sourceReviewWithSearches.searches?.filter(
        (s) => s.source.id === BuiltInImportSourceId.PUBMED,
      ) ?? []
    const searchesToCreate = []
    if (sourceReviewPubmedSearches.length > 0) {
      for (const search of sourceReviewPubmedSearches) {
        const result = await pubmedApiClient.search(search.queryTranslation!)
        searchesToCreate.push({
          query: search.searchQuery,
          date: new Date().toISOString(),
          reviewId: review.id,
          importSourceId: BuiltInImportSourceId.PUBMED,
          items: result.articles.map((a) => ({
            ...a,
            type: ReviewItemType.Article,
          })),
          queryTranslation: search.queryTranslation,
        })
      }
    }
    if (searchesToCreate.length > 0) {
      if (!sourceExistsInPlan(BuiltInImportSourceId.PUBMED, review)) {
        const pubmedSource = builtInImportSources.findById(
          BuiltInImportSourceId.PUBMED,
        )
        await reviewsService.addImportSourceToPlan(review.id, {
          name: pubmedSource.name,
          type: pubmedSource.type,
          url: pubmedSource.url ?? '',
          description: pubmedSource.description,
        })
      }
      await Promise.all(
        searchesToCreate.map((s) => reviewsService.importSearch(s)),
      )
    }
    await refresh()
    snackbar.success('Success')
  } catch (error) {
    snackbar.error('Failed to update review')
    throw error
  } finally {
    loading.stop()
  }
}

function sourceExistsInPlan(importSourceId: string, review: Review): boolean {
  const enabledSources = review.plan?.importPlan.importSources || []
  return enabledSources.some((source) => source.id === importSourceId)
}
</script>
